import React, { useState } from 'react';
import { Box, Grid, Typography, Alert } from '@mui/material';
import { useDashboardData } from '../hooks/useDashboardData';

// Componentes
import FiltroTemporal from '../components/dashboard/FiltroTemporal';
import MetricCard from '../components/dashboard/MetricCard';
import ServiciosPorDiaChart from '../components/dashboard/ServiciosPorDiaChart';
import TopServiciosChart from '../components/dashboard/TopServiciosChart';
import DistribucionEstadosChart from '../components/dashboard/DistribucionEstadosChart';
import EvolucionIngresosChart from '../components/dashboard/EvolucionIngresosChart';
import TopTrabajadoresRanking from '../components/dashboard/TopTrabajadoresRanking';

// Iconos para métricas
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

/**
 * Dashboard Analytics Principal
 * Vista de análisis con métricas, gráficos y rankings
 */
const DashboardAnalytics = () => {
  const [periodo, setPeriodo] = useState('semana');
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);

  const { data, loading, error, refetch } = useDashboardData(periodo, fechaInicio, fechaFin);

  const handlePeriodoChange = (newPeriodo, inicio, fin) => {
    setPeriodo(newPeriodo);
    setFechaInicio(inicio);
    setFechaFin(fin);
  };

  const handleFechasChange = (inicio, fin) => {
    setFechaInicio(inicio);
    setFechaFin(fin);
    // Refetch automático cuando se seleccionan ambas fechas
    if (inicio && fin) {
      setTimeout(() => refetch(), 100);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          📊 Dashboard Analytics
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Análisis y métricas de desempeño del negocio
        </Typography>
      </Box>

      {/* Filtro Temporal */}
      <FiltroTemporal
        periodo={periodo}
        onPeriodoChange={handlePeriodoChange}
        fechaInicio={fechaInicio}
        fechaFin={fechaFin}
        onFechasChange={handleFechasChange}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Métricas Principales - 4 Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Servicios Totales"
            value={data?.metricas?.servicios_totales?.cantidad}
            change={data?.metricas?.servicios_totales?.cambio_porcentual}
            icon={<AssignmentIcon />}
            type="number"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ingresos Totales"
            value={data?.metricas?.ingresos_totales?.monto}
            change={data?.metricas?.ingresos_totales?.cambio_porcentual}
            icon={<AttachMoneyIcon />}
            type="currency"
            currency={data?.moneda}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Comisiones"
            value={data?.metricas?.comisiones_totales?.monto}
            change={data?.metricas?.comisiones_totales?.cambio_porcentual}
            icon={<AccountBalanceWalletIcon />}
            type="currency"
            currency={data?.moneda}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ticket Promedio"
            value={data?.metricas?.ticket_promedio?.monto}
            change={data?.metricas?.ticket_promedio?.cambio_porcentual}
            icon={<TrendingUpIcon />}
            type="currency"
            currency={data?.moneda}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Gráficos - Primera Fila */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Servicios por Día */}
        <Grid item xs={12} lg={8}>
          <ServiciosPorDiaChart
            data={data?.serviciosPorDia}
            loading={loading}
          />
        </Grid>

        {/* Top Trabajadores */}
        <Grid item xs={12} lg={4}>
          <TopTrabajadoresRanking
            data={data?.topTrabajadores}
            currency={data?.moneda}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Gráficos - Segunda Fila */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Top Servicios */}
        <Grid item xs={12} md={6}>
          <TopServiciosChart
            data={data?.topServicios}
            loading={loading}
          />
        </Grid>

        {/* Distribución Estados */}
        <Grid item xs={12} md={6}>
          <DistribucionEstadosChart
            data={data?.distribucion}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Evolución Ingresos - Última Fila */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EvolucionIngresosChart
            data={data?.evolucion}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Métricas Secundarias (opcional - puede ir en cards pequeños) */}
      {data?.metricasSecundarias && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <MetricCard
              title="Tasa de Conversión"
              value={data.metricasSecundarias.tasa_conversion}
              icon="📈"
              type="percent"
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MetricCard
              title="Clientes Nuevos"
              value={data.metricasSecundarias.clientes_nuevos}
              icon="👥"
              type="number"
              loading={loading}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardAnalytics;
